/**
 * Extract maloneLotIdEncoded from a listing slug or resourceId.
 *
 * Listing URLs follow the pattern: /123-main-st-city-st-12345-{maloneLotId}/pad
 * The maloneLotIdEncoded is the last hyphen-separated segment of the slug.
 */
export function extractMaloneLotIdEncoded(resourceId: string): string | null {
  const parts = resourceId.split('-');
  const candidate = parts[parts.length - 1];
  return candidate || null;
}

const MALONE_LOT_ID_ENCODED_PATTERN = /^[a-z0-9]{5,}$/i;

export function isMaloneLotIdEncoded(value: string): boolean {
  return MALONE_LOT_ID_ENCODED_PATTERN.test(value);
}

export const LOCALE_SEGMENTS = new Set(['en', 'es']);

export type HdpPathnameParts = {
  resourceOrMaloneId: string;
  suffix: 'pad' | 'building';
  unit: string | null;
};

/**
 * Parse a request pathname into HDP route parts: /slug/pad, /slug/building, or
 * the 3-segment /slug/:unit/pad form, with an optional locale prefix. Null for
 * non-HDP pathnames.
 */
export function parseHdpPathname(pathname: string | null | undefined): HdpPathnameParts | null {
  if (!pathname) return null;

  const pathOnly = pathname.split(/[?#]/)[0] ?? '';
  const segments = pathOnly.split('/').filter(Boolean);
  const routeSegments = LOCALE_SEGMENTS.has(segments[0] ?? '') ? segments.slice(1) : segments;
  if (routeSegments.length !== 2 && routeSegments.length !== 3) return null;

  const resourceOrMaloneId = routeSegments[0];
  const suffix = routeSegments.at(-1);
  if (!resourceOrMaloneId || (suffix !== 'pad' && suffix !== 'building')) return null;

  return {
    resourceOrMaloneId,
    suffix,
    unit: routeSegments.length === 3 && suffix === 'pad' ? (routeSegments[1] ?? null) : null,
  };
}

/**
 * True when a request pathname is an HDP route. Used to seed SSR-only
 * decisions (e.g. hiding the mobile header) before the client HDP mount
 * effects take over.
 */
export function isHdpPathname(pathname: string | null | undefined): boolean {
  return parseHdpPathname(pathname) !== null;
}

/**
 * Extract unit from a 3-segment HDP pathname like /listing-slug/6/pad.
 *
 * App Router renders that URL through the 2-segment /listing-slug/pad route,
 * so direct loads need the original request pathname to preserve unit identity.
 */
export function extractHdpUnitFromPathname(
  pathname: string | null | undefined,
  resourceOrMaloneId: string,
): string | null {
  const parts = parseHdpPathname(pathname);
  return parts?.unit && parts.resourceOrMaloneId === resourceOrMaloneId ? parts.unit : null;
}

/**
 * Extract the listing slug (first path segment) from an HDP href like
 * `/123-main-st-city-st-12345-1abc2/pad` or `/slug/6/pad`.
 */
export function extractListingSlugFromHref(href: string | null | undefined): string | null {
  if (!href) return null;
  const pathOnly = href.split(/[?#]/)[0] ?? '';
  return pathOnly.split('/').filter(Boolean)[0] ?? null;
}

export function buildHdpPath(resourceOrMaloneId: string, suffix: 'pad' | 'building', unit?: string | null): string {
  const normalizedUnit = unit?.trim();
  return normalizedUnit ? `/${resourceOrMaloneId}/${normalizedUnit}/${suffix}` : `/${resourceOrMaloneId}/${suffix}`;
}

/** Swap the trailing lot id token of a listing slug: `…-oldid` → `…-newid`. */
export function replaceTrailingLotId(resourceOrMaloneId: string, newLotIdEncoded: string): string {
  const parts = resourceOrMaloneId.split('-');
  parts[parts.length - 1] = newLotIdEncoded;
  return parts.join('-');
}
